import { useMemo, useState } from "react";

import { ICS_FLAGS } from "@nav-games/flags";

import { BroadcastToggle } from "../../ui/BroadcastToggle";
import { Flag } from "../../ui/Flag";
import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface TranslateProps {
  onNavigate: (to: string) => void;
}

function buildSequence(text: string): string[] {
  const out: string[] = [];
  for (const ch of text.toUpperCase()) {
    if (ch === " ") continue; // hoists don't carry spaces
    if (ICS_FLAGS[ch] !== undefined) out.push(ch);
  }
  return out;
}

export function Translate({ onNavigate }: TranslateProps) {
  const [text, setText] = useState("");
  const [hidden, setHidden] = useState(false);
  const [vertical, setVertical] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const seq = useMemo(() => buildSequence(text), [text]);

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <h2 style={{ margin: 0 }}>Translate</h2>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "flags" : null} onClose={() => setHelpOpen(false)} />
      <p className="muted">
        Type a message — the maritime flag hoist appears below. Toggle to a
        vertical layout (closer to a real signal halyard). Hide the message
        and show the phone for someone else to decode.
      </p>

      {!hidden && (
        <div className="card">
          <label className="small muted" htmlFor="msg">
            Text (letters and digits)
          </label>
          <textarea
            id="msg"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="MAYDAY"
            rows={2}
            style={{
              width: "100%",
              marginTop: 4,
              padding: 8,
              fontFamily: "inherit",
              fontSize: 16,
              boxSizing: "border-box",
            }}
          />
          <p className="small muted">
            {seq.length} flag{seq.length === 1 ? "" : "s"}
          </p>
          <div className="row" style={{ gap: 8 }}>
            <button
              className="secondary"
              onClick={() => setHidden(true)}
              disabled={text.length === 0}
            >
              🙈 Hide message
            </button>
            <button
              className="secondary"
              onClick={() => setVertical((v) => !v)}
            >
              {vertical ? "Horizontal layout" : "Vertical layout"}
            </button>
          </div>
        </div>
      )}

      <div className="card" style={{ textAlign: "center" }}>
        {hidden && (
          <button
            className="secondary"
            onClick={() => setHidden(false)}
            style={{ marginBottom: 12 }}
          >
            👁 Reveal message
          </button>
        )}
        {seq.length === 0 ? (
          <p className="muted">Type a message to see flags here.</p>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: vertical ? "column" : "row",
              flexWrap: vertical ? "nowrap" : "wrap",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              overflowX: "auto",
            }}
          >
            {seq.map((c, i) => (
              <div
                key={`${i}-${c}`}
                style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
              >
                <Flag char={c} size={96} />
                {!hidden && (
                  <span className="small muted" style={{ marginTop: 4 }}>
                    {c}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <BroadcastToggle module="flags" text={text} />

      <button className="secondary" onClick={() => onNavigate("/play/flags")}>
        ← Back to Flags
      </button>
    </>
  );
}
